import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Card from "../components/Card";
import Button from "../components/Button";
import { Input } from "../components/Input";
import {
  getPlatformRouteDeclaration,
  approvePlatformRouteDeclaration,
  getRouteDeclarationOptions,
  updatePlatformRouteDeclaration,
  createPlatformSsmRevision,
} from "../services/api";
import { loadSession } from "../auth";

export default function PlatformRouteDeclarationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [decl, setDecl] = useState(null);
  const [approving, setApproving] = useState(false);
  const [successMsg, setSuccessMsg] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    employer_tenant_id: "",
    worksite_id: "",
    home_address: "",
    route_to_work_reper_1: "",
    route_to_work_reper_2: "",
    same_route_return: true,
    route_from_work_reper_1: "",
    route_from_work_reper_2: "",
    duration_minutes: "",
    vehicles: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [options, setOptions] = useState(null);
  const session = loadSession();
  const me = session?.user;
  const isPlatformAdmin = me?.derived?.isPlatformAdmin || (Array.isArray(me?.roles) && me.roles.includes("platform_admin"));

  function getEmployerTenantId(employer) {
    return employer?.id ?? employer?.tenant_id ?? employer?.tenantId ?? employer?.employer_tenant_id ?? null;
  }

  function getWorksiteTenantId(worksite) {
    return worksite?.tenant_id ?? worksite?.employer_tenant_id ?? worksite?.tenantId ?? worksite?.employerTenantId ?? worksite?.employer_id ?? null;
  }

  function getDeclEmployerTenantId(d) {
    if (!d) return null;
    return d.employer_tenant_id ?? d.tenant_id ?? d.tenantId ?? d.employer_id ?? d.employer?.id ?? d.employer?.tenant_id ?? d.employer?.tenantId ?? null;
  }

  function getDeclWorksiteId(d) {
    if (!d) return null;
    return d.worksite_id ?? d.worksite?.id ?? d.worksiteId ?? d.worksite?.worksite_id ?? d.worksite?.tenant_id ?? null;
  }

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getPlatformRouteDeclaration(id);
        if (!alive) return;
        setDecl(data.declaration ?? data);
        // Load admin options when available (employers/worksites)
        try {
          const opts = await getRouteDeclarationOptions();
          if (!alive) return;
          setOptions(opts);
        } catch (e) {
          // ignore missing options
        }
      } catch (e) {
        if (!alive) return;
        setError(e?.message || "Eroare la încărcare");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [id]);

  // Derived helper values for edit-mode dropdowns
  const employersList = options?.employers ?? [];
  const worksitesList = options?.worksites ?? [];

  function idEquals(a, b) {
    const sa = String(a ?? "").trim();
    const sb = String(b ?? "").trim();
    if (!sa || !sb) return false;
    const na = Number(sa);
    const nb = Number(sb);
    if (Number.isFinite(na) && Number.isFinite(nb)) return na === nb;
    return sa === sb;
  }

  const employerSelected = String(form.employer_tenant_id || "").trim() !== "";
  const worksitesHaveEmployer = worksitesList.some((w) => {
    const wid = getWorksiteTenantId(w);
    return wid !== null && wid !== undefined && String(wid).trim() !== "";
  });

  const filteredWorksites = employerSelected
    ? (worksitesHaveEmployer
      ? worksitesList.filter((w) => idEquals(getWorksiteTenantId(w), form.employer_tenant_id))
      : (employersList.length === 1 ? worksitesList : []))
    : [];

  const selectedWorksite = worksitesList.find((x) => idEquals(x.id, form.worksite_id)) || null;
  const editModeWorksiteCity = form.worksite_id ? (selectedWorksite?.city ?? "") : "";

  // Build select option lists with fallbacks to declaration snapshots when options are missing
  const employerSelectOptions = (options?.employers ?? []).map((em) => ({ value: String(getEmployerTenantId(em) ?? ""), label: em.name }));
  if (employerSelectOptions.length === 0) {
    // Try to derive employer options from worksites when explicit employers list is missing
    const byEmployer = new Map();
    (worksitesList || []).forEach((w) => {
      const val = String(getWorksiteTenantId(w) ?? "");
      if (!val) return;
      if (!byEmployer.has(val)) {
        const label = w.employer_name || w.employer_name_snapshot || w.employer?.name || w.employer?.employer_name || val;
        byEmployer.set(val, { value: val, label });
      }
    });
    if (byEmployer.size > 0) employerSelectOptions.push(...Array.from(byEmployer.values()));
    else if (decl?.employer_tenant_id) {
      const val = String(getDeclEmployerTenantId(decl) ?? decl.employer_tenant_id ?? "");
      employerSelectOptions.push({ value: val, label: decl.employer_name_snapshot ?? decl.employer_name ?? "" });
    }
  }

  // Use the already-filtered worksite list. Worksites now include tenant_id so the filter is exact.
  // Do not fall back to all worksites: that hid the real problem in the backend options payload.
  const filteredWorksitesForSelect = filteredWorksites;

  

  async function enterEditMode() {
    if (!decl) return;
    // Try to load options before showing form (best-effort)
    if (!options) {
      try {
        const opts = await getRouteDeclarationOptions();
        setOptions(opts);
      } catch (e) {
        // ignore
      }
    }
    const declEmp = getDeclEmployerTenantId(decl);
    const declWs = getDeclWorksiteId(decl);
    setForm({
      full_name: decl.full_name || "",
      employer_tenant_id: declEmp !== undefined && declEmp !== null ? String(declEmp) : String(decl.employer_tenant_id ?? ""),
      worksite_id: declWs !== undefined && declWs !== null ? String(declWs) : String(decl.worksite_id ?? ""),
      home_address: decl.home_address || "",
      route_to_work_reper_1: decl.route_to_work_reper_1 || "",
      route_to_work_reper_2: decl.route_to_work_reper_2 || "",
      same_route_return: typeof decl.same_route_return === 'boolean' ? decl.same_route_return : true,
      route_from_work_reper_1: decl.route_from_work_reper_1 || "",
      route_from_work_reper_2: decl.route_from_work_reper_2 || "",
      duration_minutes: decl.duration_minutes ? String(decl.duration_minutes) : "",
      vehicles: decl.vehicles || "",
    });
    setFormErrors({});
    setSuccessMsg(null);
    setEditMode(true);
  }

  function cancelEdit() {
    setEditMode(false);
    setFormErrors({});
  }

  function validateForm() {
    const errs = {};
    const trim = (s) => String(s || "").trim();
    const effectiveEmployer = getEffectiveEmployerTenantId();
    if (!trim(form.full_name)) errs.full_name = "Nume complet este obligatoriu.";
    if (!effectiveEmployer) errs.employer_tenant_id = "Angajator este obligatoriu.";
    if (!trim(form.worksite_id)) errs.worksite_id = "Punct de lucru este obligatoriu.";
    if (!trim(form.home_address)) errs.home_address = "Adresă domiciliu este obligatoriu.";
    if (!trim(form.route_to_work_reper_1)) errs.route_to_work_reper_1 = "Reper traseu către locul de muncă 1 este obligatoriu.";
    const dur = Number(String(form.duration_minutes || "").trim());
    if (!String(form.duration_minutes || "").trim() || Number.isNaN(dur) || dur <= 0) errs.duration_minutes = "Introduceți o durată validă.";
    if (!trim(form.vehicles)) errs.vehicles = "Mijloace de deplasare este obligatoriu.";
    if (!form.same_route_return && !trim(form.route_from_work_reper_1)) errs.route_from_work_reper_1 = "Reper traseu retur 1 este obligatoriu.";
    return errs;
  }

  function getEffectiveEmployerTenantId() {
    const explicit = String(form.employer_tenant_id || "").trim();
    if (explicit) return explicit;
    if (form.worksite_id && options?.worksites?.length) {
      const ws = options.worksites.find((x) => String(x.id) === String(form.worksite_id));
      const maybe = ws ? (ws?.employer_tenant_id ?? ws?.tenant_id ?? ws?.employer_id ?? null) : null;
      if (maybe !== null && maybe !== undefined) return String(maybe);
    }
    const def = options?.defaults?.employer_tenant_id;
    if (def !== undefined && def !== null) return String(def);
    return "";
  }

  async function handleSave(e) {
    e?.preventDefault?.();
    setFormErrors({});
    setError(null);
    const errs = validateForm();
    if (Object.keys(errs).length) {
      setFormErrors(errs);
      return;
    }
    let finalEmployer = getEffectiveEmployerTenantId();
    // If employer couldn't be derived, try refreshing options once and derive from fresh data.
    if (!finalEmployer && form.worksite_id) {
      let fresh = null;
      try {
        fresh = await getRouteDeclarationOptions();
        setOptions(fresh);
      } catch (e) {
        // ignore
      }
      if (fresh) {
        const explicit = String(form.employer_tenant_id || "").trim();
        if (!explicit) {
          const ws = fresh.worksites?.find((x) => String(x.id) === String(form.worksite_id));
          const maybe = ws ? (ws?.employer_tenant_id ?? ws?.tenant_id ?? ws?.employer_id ?? null) : null;
          if (maybe !== null && maybe !== undefined) {
            finalEmployer = String(maybe);
          } else {
            const def = fresh.defaults?.employer_tenant_id ?? fresh.current_user?.tenant_id ?? fresh.current_user?.tenantId ?? (fresh.employers && fresh.employers.length === 1 ? fresh.employers[0].id : null);
            if (def !== undefined && def !== null) finalEmployer = String(def);
          }
        } else {
          finalEmployer = explicit;
        }
      }
    }

    // Validate that an employer/worksite is selected (allow string IDs)
    const finalEmployerRaw = String(finalEmployer ?? "").trim();
    if (!finalEmployerRaw) {
      setFormErrors({ employer_tenant_id: "Angajator este obligatoriu." });
      setSaving(false);
      return;
    }
    const finalWorksiteRaw = String(form.worksite_id ?? "").trim();
    if (!finalWorksiteRaw) {
      setFormErrors({ worksite_id: "Punct de lucru este obligatoriu." });
      setSaving(false);
      return;
    }

    function coerceIdForPayload(raw) {
      const s = String(raw ?? "").trim();
      if (s === "") return null;
      const n = Number(s);
      if (Number.isFinite(n) && n > 0) return n;
      return s;
    }

    const employerPayload = coerceIdForPayload(finalEmployerRaw);
    const worksitePayload = coerceIdForPayload(finalWorksiteRaw);

    const payload = {
      full_name: String(form.full_name || "").trim(),
      employer_tenant_id: employerPayload,
      worksite_id: worksitePayload,
      home_address: String(form.home_address || "").trim(),
      route_to_work_reper_1: String(form.route_to_work_reper_1 || "").trim(),
      route_to_work_reper_2: form.route_to_work_reper_2 ? String(form.route_to_work_reper_2).trim() : null,
      same_route_return: !!form.same_route_return,
      route_from_work_reper_1: form.same_route_return ? null : (form.route_from_work_reper_1 ? String(form.route_from_work_reper_1).trim() : null),
      route_from_work_reper_2: form.same_route_return ? null : (form.route_from_work_reper_2 ? String(form.route_from_work_reper_2).trim() : null),
      duration_minutes: form.duration_minutes ? Number(form.duration_minutes) : null,
      vehicles: String(form.vehicles || "").trim(),
    };

    setSaving(true);
    try {
      await updatePlatformRouteDeclaration(id, payload);
      setSuccessMsg("Declarația de traseu a fost actualizată.");
      // refetch detail
      try {
        const data = await getPlatformRouteDeclaration(id);
        setDecl(data.declaration ?? data);
      } catch (e) {
        // ignore
      }
      setEditMode(false);
    } catch (e) {
      setError(e?.message || "Eroare la salvare");
    } finally {
      setSaving(false);
    }
  }

  async function handleApprove() {
    if (!window.confirm("Sunteți sigur că doriți să aproba această declarație?")) return;
    setApproving(true);
    setError(null);
    try {
      await approvePlatformRouteDeclaration(id);
      // refetch the declaration to get updated status
      try {
        const data = await getPlatformRouteDeclaration(id);
        setDecl(data.declaration ?? data);
      } catch (e) {
        // ignore
      }
      setSuccessMsg("Declarația de traseu a fost aprobată.");
      setEditMode(false);
    } catch (e) {
      setError(e?.message || "Eroare la aprobare");
    } finally {
      setApproving(false);
    }
  }

  async function handleGenerateSsm() {
    if (!decl) return;
    setGenerating(true);
    setError(null);
    try {
      const rev = await createPlatformSsmRevision(id);
      // Refresh declaration detail
      try {
        const data = await getPlatformRouteDeclaration(id);
        setDecl(data.declaration ?? data);
      } catch (e) {
        // ignore
      }
      // Notify any open revision lists to refresh
      try {
        const uid = rev?.user_id ?? decl?.user_id;
        window.dispatchEvent(new CustomEvent("ssmRevisionCreated", { detail: { userId: uid } }));
      } catch (e) {}
      setSuccessMsg("Revizia SSM a fost generată.");
    } catch (e) {
      // Map known backend errors to friendly messages
      const code = e?.status;
      const msg = (e?.message || "").toString();
      if (code === 400 && msg.includes("pending_approval")) {
        alert("Declarația este în așteptare pentru aprobare.");
      } else if (code === 409 || msg.includes("conflict")) {
        alert("Există deja o revizie generată care trebuie finalizată înainte de a genera următoarea revizie.");
        // trigger refresh of revision lists
        try { window.dispatchEvent(new CustomEvent("ssmRevisionCreated", { detail: { userId: decl?.user_id } })); } catch (e) {}
      } else if (code === 400 && msg.includes("max_slots_exceeded")) {
        alert("Șablonul actual permite maximum 3 declarații de traseu.");
      } else {
        alert(e?.message || "Eroare la generare fișă SSM");
      }
    } finally {
      setGenerating(false);
    }
  }

  if (loading) return <div className="max-w-6xl mx-auto px-4 py-6"><Card><div className="text-sm text-slate-500">Se încarcă…</div></Card></div>;

  if (error) return <div className="max-w-6xl mx-auto px-4 py-6"><Card><div className="text-sm text-red-600">{error}</div></Card></div>;

  if (!decl) return <div className="max-w-6xl mx-auto px-4 py-6"><Card><div className="text-sm text-slate-500">Declarația nu a fost găsită.</div></Card></div>;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold text-slate-900 mb-2">Revizuire declarație de traseu</h1>
      <Card>
        <div className="space-y-3">
          {!editMode ? (
            <>
              <div>
                <div className="text-xs text-slate-500">Nume complet</div>
                <div className="font-medium text-slate-900">{decl.full_name}</div>
              </div>

              <div>
                <div className="text-xs text-slate-500">Adresă domiciliu</div>
                <div className="font-medium text-slate-900">{decl.home_address}</div>
              </div>

              <div>
                <div className="text-xs text-slate-500">Angajator</div>
                <div className="font-medium text-slate-900">{decl.employer_name_snapshot || decl.employer_name}</div>
              </div>

              <div>
                <div className="text-xs text-slate-500">Punct de lucru</div>
                <div className="font-medium text-slate-900">{decl.worksite_name_snapshot || decl.worksite_name}</div>
                <div className="text-xs text-slate-500">Oraș</div>
                <div className="font-medium text-slate-900">{decl.worksite_city_snapshot || decl.worksite_city}</div>
              </div>

              <div>
                <div className="text-xs text-slate-500">Reper traseu către locul de muncă 1</div>
                <div className="font-medium text-slate-900">{decl.route_to_work_reper_1}</div>
              </div>

              {decl.route_to_work_reper_2 && (
                <div>
                  <div className="text-xs text-slate-500">Reper traseu către locul de muncă 2</div>
                  <div className="font-medium text-slate-900">{decl.route_to_work_reper_2}</div>
                </div>
              )}

              <div>
                <div className="text-xs text-slate-500">Traseu de întoarcere</div>
                {decl.same_route_return ? (
                  <div className="font-medium text-slate-900">Traseul de întoarcere este același.</div>
                ) : (
                  <div>
                    <div className="text-xs text-slate-500">Reper retur 1</div>
                    <div className="font-medium text-slate-900">{decl.route_from_work_reper_1}</div>
                    {decl.route_from_work_reper_2 && (
                      <>
                        <div className="text-xs text-slate-500">Reper retur 2</div>
                        <div className="font-medium text-slate-900">{decl.route_from_work_reper_2}</div>
                      </>
                    )}
                  </div>
                )}
              </div>

              <div>
                <div className="text-xs text-slate-500">Durată deplasare</div>
                <div className="font-medium text-slate-900">{decl.duration_minutes} minute</div>
              </div>

              <div>
                <div className="text-xs text-slate-500">Mijloace de deplasare</div>
                <div className="font-medium text-slate-900">{decl.vehicles}</div>
              </div>

              <div>
                <div className="text-xs text-slate-500">Status</div>
                <div className="font-medium text-slate-900">{decl.status}</div>
              </div>

              <div>
                <div className="text-xs text-slate-500">Trimis pentru aprobare</div>
                <div className="font-medium text-slate-900">{decl.submitted_for_approval_at || decl.created_at}</div>
              </div>

              <div className="pt-3">
                <Button onClick={() => navigate(-1)} variant="ghost" className="mr-2">Înapoi</Button>
                {decl.status === 'pending_approval' && (
                  <>
                    <Button onClick={enterEditMode} variant="secondary" className="mr-2">Editează</Button>
                    <Button onClick={handleApprove} disabled={approving}>{approving ? "Se aprobă..." : "Aprobă declarația"}</Button>
                  </>
                )}
                {(isPlatformAdmin && (decl.status === 'approved' || decl.status === 'signed')) && (
                  <Button onClick={handleGenerateSsm} className="ml-2" disabled={generating}>
                    {generating ? "Se generează..." : "Generează fișa SSM"}
                  </Button>
                )}
              </div>

              {decl.status !== 'pending_approval' && (
                <div className="text-sm text-slate-500">Declarațiile aprobate sau semnate nu pot fi editate.</div>
              )}
            </>
          ) : (
            <form onSubmit={handleSave} className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-xs text-slate-500 font-medium mb-1">Nume complet</label>
                <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
                {formErrors.full_name && <div className="text-sm text-red-600 mt-1">{formErrors.full_name}</div>}
              </div>

              <div>
                <label className="block text-xs text-slate-500 font-medium mb-1">Angajator</label>
                {employerSelectOptions.length ? (
                  <select className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none" value={form.employer_tenant_id} onChange={(e) => setForm({ ...form, employer_tenant_id: e.target.value, worksite_id: "" })}>
                    <option value="">-- Selectați --</option>
                    {employerSelectOptions.map((opt) => <option key={opt.value || opt.label} value={opt.value}>{opt.label}</option>)}
                  </select>
                ) : (
                  <select className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 outline-none" value={form.employer_tenant_id} disabled onChange={() => {}}>
                    <option value="">{decl.employer_name_snapshot || decl.employer_name || "--"}</option>
                  </select>
                )}
                {formErrors.employer_tenant_id && <div className="text-sm text-red-600 mt-1">{formErrors.employer_tenant_id}</div>}
              </div>

              <div>
                <label className="block text-xs text-slate-500 font-medium mb-1">Punct de lucru</label>
                {!employerSelected ? (
                  <select className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-400 outline-none" value="" disabled onChange={() => {}}>
                    <option value="">Selectați mai întâi angajatorul</option>
                  </select>
                ) : filteredWorksitesForSelect.length > 0 ? (
                  <select className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none" value={form.worksite_id} onChange={(e) => setForm({ ...form, worksite_id: e.target.value })}>
                    <option value="">-- Selectați --</option>
                    {filteredWorksitesForSelect.map((w) => <option key={String(w.id)} value={String(w.id)}>{w.name}</option>)}
                  </select>
                ) : (
                  <div className="text-sm text-amber-700 py-2">Nu există puncte de lucru pentru angajatorul selectat.</div>
                )}
                {formErrors.worksite_id && <div className="text-sm text-red-600 mt-1">{formErrors.worksite_id}</div>}
                <div className="text-xs text-slate-500 mt-1">Oraș: <span className="font-medium text-slate-900">{editMode ? (editModeWorksiteCity || "") : (decl.worksite_city_snapshot || decl.worksite_city)}</span></div>
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs text-slate-500 font-medium mb-1">Adresă domiciliu</label>
                <Input value={form.home_address} onChange={(e) => setForm({ ...form, home_address: e.target.value })} />
                {formErrors.home_address && <div className="text-sm text-red-600 mt-1">{formErrors.home_address}</div>}
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs text-slate-500 font-medium mb-1">Reper traseu către locul de muncă 1</label>
                <Input value={form.route_to_work_reper_1} onChange={(e) => setForm({ ...form, route_to_work_reper_1: e.target.value })} />
                {formErrors.route_to_work_reper_1 && <div className="text-sm text-red-600 mt-1">{formErrors.route_to_work_reper_1}</div>}
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs text-slate-500 font-medium mb-1">Reper traseu către locul de muncă 2</label>
                <Input value={form.route_to_work_reper_2} onChange={(e) => setForm({ ...form, route_to_work_reper_2: e.target.value })} />
              </div>

              <div className="sm:col-span-2">
                <label className="inline-flex items-center gap-2">
                  <input type="checkbox" checked={!!form.same_route_return} onChange={(e) => setForm({ ...form, same_route_return: e.target.checked })} />
                  <span className="text-sm text-slate-700 ml-2">Traseul de întoarcere este același</span>
                </label>
              </div>

              {!form.same_route_return && (
                <>
                  <div>
                    <label className="block text-xs text-slate-500 font-medium mb-1">Reper traseu retur 1</label>
                    <Input value={form.route_from_work_reper_1} onChange={(e) => setForm({ ...form, route_from_work_reper_1: e.target.value })} />
                    {formErrors.route_from_work_reper_1 && <div className="text-sm text-red-600 mt-1">{formErrors.route_from_work_reper_1}</div>}
                  </div>

                  <div>
                    <label className="block text-xs text-slate-500 font-medium mb-1">Reper traseu retur 2</label>
                    <Input value={form.route_from_work_reper_2} onChange={(e) => setForm({ ...form, route_from_work_reper_2: e.target.value })} />
                  </div>
                </>
              )}

              <div>
                <label className="block text-xs text-slate-500 font-medium mb-1">Durată deplasare</label>
                <div className="flex items-center gap-2">
                  <Input value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} />
                  <div className="text-sm text-slate-500">minute</div>
                </div>
                {formErrors.duration_minutes && <div className="text-sm text-red-600 mt-1">{formErrors.duration_minutes}</div>}
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs text-slate-500 font-medium mb-1">Mijloace de deplasare</label>
                <Input value={form.vehicles} onChange={(e) => setForm({ ...form, vehicles: e.target.value })} />
                {formErrors.vehicles && <div className="text-sm text-red-600 mt-1">{formErrors.vehicles}</div>}
              </div>

              <div className="sm:col-span-2">
                <Button type="button" variant="ghost" className="mr-2" onClick={cancelEdit}>Anulează</Button>
                <Button type="submit" disabled={saving}>{saving ? "Se salvează..." : "Salvează modificările"}</Button>
                {decl.status === 'pending_approval' && (
                  <Button onClick={handleApprove} disabled={approving} className="ml-2">{approving ? "Se aprobă..." : "Aprobă declarația"}</Button>
                )}
              </div>

              {error && <div className="text-sm text-red-600 mt-1 sm:col-span-2">{error}</div>}
              {successMsg && <div className="text-sm text-green-700 mt-1 sm:col-span-2">{successMsg}</div>}
            </form>
          )}
        </div>
      </Card>
    </div>
  );
}
